import { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import useGlobalNotification from '@utils/hooks/useGlobalNotification';
import {
    AgendaResource,
    useHumanResources,
    useResourcesAtom,
    useSelectedResourcesIds,
    useSelectedResources,
    useSelectedResourcesByAgendaView,
    usePhysicalResources,
    useResourcesLoading,
    useResourcesAsArray,
    AgendaResources,
    useMkpResourcesAtom,
} from '@states/resources';
import {
    getResources,
    firstSelectableResource,
} from '@utils/network/resources';
import { resource as resourceAPI } from '@utils/network/network';
import logs from '@utils/logs/firebase';

export type UseResourcesR = {
    isLoading: boolean;
    selectedResourcesId: number[];
    selectedResources: AgendaResource[];
    selectedResourcesByAgendaView: AgendaResource[];
    removeSelectedResourceId: (id: number) => void;
    addSelectedResourceId: (id: number) => void;
    toggleResource: (id: number) => void;
    forceSelectResource: (id: number) => void;
    fetchResources: () => Promise<void>;
    rawResourcesAsArray: AgendaResource[];
    humanResources: AgendaResource[];
    physicalResources: AgendaResource[];
    resources: AgendaResources;
    mkpResourceIds: number[];
    fetchMkpResourceIds: (companyCode: string) => void;
};

export type UseResource = (initialFetch?: boolean) => UseResourcesR;

const useResources: UseResource = (initialFetch = false) => {
    const intl = useIntl();
    const { setNotification } = useGlobalNotification();
    const { value: isLoading, set: setLoading } = useResourcesLoading();
    const {
        value: selectedResourcesId,
        set: setSelectedResourcesId,
    } = useSelectedResourcesIds();
    const { get: rawResourcesAsArray } = useResourcesAsArray();
    const { get: selectedResources } = useSelectedResources();
    const {
        get: selectedResourcesByAgendaView,
    } = useSelectedResourcesByAgendaView();
    const { value: resources, set: setResourceAtom } = useResourcesAtom();
    const { get: humanResources } = useHumanResources();
    const { get: physicalResources } = usePhysicalResources();
    const {
        value: mkpResourceIds,
        set: setMkpResourceIds,
    } = useMkpResourcesAtom();

    const fetchMkpResourceIds = useCallback(
        async (companyCode) => {
            const res = await resourceAPI.getMarketplaceConnectedResourceIds(
                companyCode,
            );
            if (res.data) {
                setMkpResourceIds(res.data.map(Number) || []);
            }
        },
        [setMkpResourceIds],
    );

    const removeSelectedResourceId = useCallback(
        (id: number) =>
            setSelectedResourcesId(
                selectedResourcesId.filter((selectedId) => selectedId !== id),
            ),
        [selectedResourcesId, setSelectedResourcesId],
    );

    const addSelectedResourceId = useCallback(
        (id: number) => setSelectedResourcesId((current) => [...current, id]),
        [setSelectedResourcesId],
    );

    const toggleResource = useCallback(
        (id: number): void => {
            if (selectedResourcesId.find((selected) => selected === id)) {
                removeSelectedResourceId(id);
            } else {
                logs.selectSeveralResources();
                addSelectedResourceId(id);
            }
        },
        [addSelectedResourceId, removeSelectedResourceId, selectedResourcesId],
    );

    const forceSelectResource = useCallback(
        (id: number) => {
            if (selectedResourcesId.find((selected) => selected === id)) return;
            toggleResource(id);
        },
        [selectedResourcesId, toggleResource],
    );

    const fetchResources = useCallback(async () => {
        try {
            setLoading(true);
            const agendaResources = await getResources();
            setResourceAtom(agendaResources);
            // Remove non present selected resources to avoid blocking resource selection
            selectedResourcesId.forEach(
                (sId) => !agendaResources[sId] && removeSelectedResourceId(sId),
            );
            // Add a default resource if no resources are selected
            if (selectedResourcesId.length < 1) {
                const selectable = firstSelectableResource(agendaResources);
                if (selectable) {
                    toggleResource(selectable.id);
                }
            }
            setLoading(false);
        } catch (e) {
            setLoading(false);
            setNotification(
                'error',
                intl.formatMessage({
                    id: 'page.agenda.resources.get.error',
                    defaultMessage: 'Impossible de charger les employées',
                }),
                true,
            );
        }
    }, [
        intl,
        removeSelectedResourceId,
        selectedResourcesId,
        setLoading,
        setNotification,
        setResourceAtom,
        toggleResource,
    ]);

    useEffect(() => {
        if (initialFetch) {
            void fetchResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFetch]);

    return {
        isLoading,
        selectedResourcesId,
        selectedResources,
        selectedResourcesByAgendaView,
        addSelectedResourceId,
        removeSelectedResourceId,
        toggleResource,
        forceSelectResource,
        fetchResources,
        rawResourcesAsArray,
        humanResources,
        physicalResources,
        resources,
        mkpResourceIds,
        fetchMkpResourceIds,
    };
};

export default useResources;
